import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, TextField, Button,
  CircularProgress, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, Stack, Alert, Paper, Divider,
  Dialog, DialogTitle, DialogContent, DialogActions,
  IconButton, Tooltip, Tab, Tabs, MenuItem, InputAdornment
} from '@mui/material';
import {
  AdminPanelSettings, School, People, CheckCircle, Block,
  Delete, Add, Refresh, Search, Visibility, VisibilityOff,
  BarChart, Assignment, Build, ContentCopy, Check, Warning
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import axios from 'axios';

const api = axios.create({ baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000/api' });
api.interceptors.request.use(c => {
  const t = localStorage.getItem('platform_token');
  if (t) c.headers.Authorization = `Bearer ${t}`;
  return c;
});

const STATUS_COLOR = { active:'success', pending:'warning', suspended:'error', terminated:'default' };
const fmtD = d => d ? new Date(d).toLocaleDateString('en-GB', { day:'numeric', month:'short', year:'numeric' }) : '—';
const ptLabel = v => (v||'').replace(/_/g,' ').replace(/\b\w/g,l=>l.toUpperCase());
const PLATFORM_URL = process.env.REACT_APP_API_URL?.replace('/api','') || 'http://localhost:3000';

function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ pt:3 }}>{children}</Box> : null;
}

function CopyBtn({ text }) {
  const [ok, setOk] = useState(false);
  return (
    <Tooltip title={ok?'Copied!':'Copy'}>
      <IconButton size="small" onClick={() => { navigator.clipboard.writeText(text); setOk(true); setTimeout(()=>setOk(false),2000); }}>
        {ok ? <Check fontSize="small" color="success" /> : <ContentCopy fontSize="small" />}
      </IconButton>
    </Tooltip>
  );
}

// ── Login page ─────────────────────────────────────────────────────────────────
function PlatformLogin({ onLogin }) {
  const [form, setForm] = useState({ username:'', password:'' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPw, setShowPw] = useState(false);

  const submit = async () => {
    setLoading(true); setError('');
    try {
      const r = await api.post('/platform/auth/login', form);
      localStorage.setItem('platform_token', r.data.token);
      onLogin(r.data.user);
    } catch (err) { setError(err.response?.data?.message || 'Login failed'); }
    finally { setLoading(false); }
  };

  return (
    <Box sx={{ minHeight:'100vh', bgcolor:'#0f2b4a', display:'flex', alignItems:'center', justifyContent:'center' }}>
      <Card sx={{ maxWidth:380, width:'100%', borderRadius:3, mx:2 }}>
        <CardContent sx={{ p:4 }}>
          <Stack alignItems="center" sx={{ mb:3 }}>
            <AdminPanelSettings sx={{ fontSize:48, color:'#0f2b4a', mb:1 }} />
            <Typography variant="h6" fontWeight={800}>Platform Admin</Typography>
            <Typography variant="body2" color="text.secondary">School Management SaaS Console</Typography>
          </Stack>
          {error && <Alert severity="error" sx={{ mb:2, fontSize:12 }}>{error}</Alert>}
          <Stack spacing={2}>
            <TextField size="small" fullWidth label="Username or Email" value={form.username} onChange={e => setForm(f => ({...f, username:e.target.value}))} />
            <TextField size="small" fullWidth label="Password" type={showPw?'text':'password'} value={form.password} onChange={e => setForm(f => ({...f, password:e.target.value}))}
              onKeyDown={e => e.key==='Enter'&&submit()}
              InputProps={{ endAdornment:<InputAdornment position="end"><IconButton size="small" onClick={()=>setShowPw(v=>!v)}>{showPw?<VisibilityOff fontSize="small"/>:<Visibility fontSize="small"/>}</IconButton></InputAdornment> }} />
            <Button variant="contained" fullWidth size="large" onClick={submit} disabled={loading} sx={{ bgcolor:'#0f2b4a', py:1.5 }}>
              {loading ? <CircularProgress size={20} color="inherit" /> : 'Sign In'}
            </Button>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}

// ── Overview Tab ───────────────────────────────────────────────────────────────
function OverviewTab() {
  const { enqueueSnackbar } = useSnackbar();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/platform/stats')
      .then(r => { if (r.data.success) setData(r.data.data); })
      .catch(() => enqueueSnackbar('Failed to load', { variant:'error' }))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <CircularProgress />;
  const s = data?.stats;

  return (
    <Grid container spacing={2}>
      {[
        { label:'Total Schools', value:s?.total_tenants||0, color:'#0f2b4a' },
        { label:'Active', value:s?.active||0, color:'#2e7d32' },
        { label:'Pending', value:s?.pending||0, color:'#e65100' },
        { label:'Suspended', value:s?.suspended||0, color:'#c62828' },
        { label:'Provisioned DBs', value:s?.provisioned||0, color:'#1565c0' },
        { label:'Pending Requests', value:s?.pending_requests||0, color:'#ad1457', alert:s?.pending_requests>0 },
      ].map(c => (
        <Grid item xs={6} sm={4} md={2} key={c.label}>
          <Card variant="outlined" sx={{ borderRadius:2, borderLeft:`4px solid ${c.color}` }}>
            <CardContent sx={{ pb:'12px !important' }}>
              <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" fontSize={9}>{c.label}</Typography>
              <Typography variant="h5" fontWeight={800} sx={{ color: c.alert&&c.value>0 ? '#c62828' : c.color }}>{c.value}</Typography>
            </CardContent>
          </Card>
        </Grid>
      ))}

      {s?.pending_requests > 0 && (
        <Grid item xs={12}>
          <Alert severity="warning">
            <strong>{s.pending_requests} registration request(s)</strong> are awaiting review. Go to the Requests tab to approve or reject.
          </Alert>
        </Grid>
      )}

      <Grid item xs={12} md={6}>
        <Card variant="outlined" sx={{ borderRadius:2 }}>
          <CardContent>
            <Typography fontWeight={700} gutterBottom>Recently Registered Schools</Typography>
            {data?.recent_schools?.map((sch, i) => (
              <Box key={sch.id} sx={{ display:'flex', justifyContent:'space-between', py:0.6, borderBottom:'1px solid #f5f5f5' }}>
                <Box><Typography fontSize={13} fontWeight={600}>{sch.school_name}</Typography><Typography fontSize={10} color="text.secondary">{sch.slug} · {fmtD(sch.created_at)}</Typography></Box>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Chip label={ptLabel(sch.status)} size="small" color={STATUS_COLOR[sch.status]||'default'} sx={{ fontSize:9 }} />
                  {!sch.db_provisioned && <Chip label="No DB" size="small" color="error" sx={{ fontSize:9 }} />}
                </Stack>
              </Box>
            ))}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
}

// ── Schools (Tenants) Tab ─────────────────────────────────────────────────────
function SchoolsTab() {
  const { enqueueSnackbar } = useSnackbar();
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [filterStatus, setFStatus] = useState('');
  const [actionDialog, setActionDialog] = useState(null);
  const [actionForm, setActionForm] = useState({ action:'', reason:'', plan:'' });
  const [detailOpen, setDetailOpen] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/platform/tenants', { params: { status: filterStatus||undefined, search: search||undefined } });
      if (r.data.success) setSchools(r.data.data);
    } catch { enqueueSnackbar('Failed to load', { variant:'error' }); }
    finally { setLoading(false); }
  }, [filterStatus, search, enqueueSnackbar]);

  useEffect(() => { load(); }, [load]);

  const doAction = async () => {
    try {
      if (actionForm.action === 'fix_settings') {
        await api.post(`/platform/tenants/${actionDialog.id}/fix-settings`);
        enqueueSnackbar('School name fixed in settings', { variant: 'success' });
        setActionDialog(null); return;
      }
      await api.put(`/platform/tenants/${actionDialog.id}/action`, actionForm);
      enqueueSnackbar(`Action applied`, { variant:'success' });
      setActionDialog(null); load();
    } catch (err) { enqueueSnackbar(err.response?.data?.message || 'Error', { variant:'error' }); }
  };

  const schoolUrl = slug => `${PLATFORM_URL}/school/${slug}`;

  return (
    <Box>
      <Stack direction="row" spacing={2} sx={{ mb:2 }} flexWrap="wrap" alignItems="center">
        <TextField size="small" placeholder="Search…" value={search} onChange={e => setSearch(e.target.value)}
          InputProps={{ startAdornment:<InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }} sx={{ minWidth:200 }} />
        <TextField select size="small" label="Status" value={filterStatus} onChange={e => setFStatus(e.target.value)} sx={{ minWidth:140 }}>
          <MenuItem value="">All</MenuItem>
          {['active','pending','suspended','terminated'].map(s => <MenuItem key={s} value={s}>{ptLabel(s)}</MenuItem>)}
        </TextField>
        <Box sx={{ flexGrow:1 }} />
        <Tooltip title="Refresh"><IconButton size="small" onClick={load}><Refresh /></IconButton></Tooltip>
      </Stack>

      {loading ? <CircularProgress /> : (
        <Card variant="outlined" sx={{ borderRadius:2 }}>
          <TableContainer sx={{ maxHeight:520 }}>
            <Table size="small" stickyHeader>
              <TableHead><TableRow>
                {['School','Slug / DB','Region','Admin','Plan','Status','DB','URL','Actions'].map(h => (
                  <TableCell key={h} sx={{ bgcolor:'#0f2b4a', color:'white', fontWeight:700, fontSize:11 }}>{h}</TableCell>
                ))}
              </TableRow></TableHead>
              <TableBody>
                {schools.length === 0 && <TableRow><TableCell colSpan={9} align="center" sx={{ py:4, color:'text.secondary' }}>No schools found</TableCell></TableRow>}
                {schools.map((sch, i) => (
                  <TableRow key={sch.id} hover sx={{ bgcolor: sch.status==='terminated'?'#fafafa': sch.status==='suspended'?'#fff8f0': i%2===0?'white':'#fafafa' }}>
                    <TableCell>
                      <Typography fontSize={13} fontWeight={600}>{sch.school_name}</Typography>
                      <Typography fontSize={10} color="text.secondary">{sch.school_type} · {fmtD(sch.created_at)}</Typography>
                    </TableCell>
                    <TableCell sx={{ fontFamily:'monospace', fontSize:11 }}>
                      <Typography fontSize={11}>{sch.slug}</Typography>
                      <Typography fontSize={9} color="text.secondary">{sch.db_name}</Typography>
                    </TableCell>
                    <TableCell sx={{ fontSize:11 }}>{sch.city||''}{sch.region?`, ${sch.region}`:''}<br/><Typography component="span" fontSize={9} color="text.secondary">{sch.country}</Typography></TableCell>
                    <TableCell>
                      <Typography fontSize={12}>{sch.admin_name}</Typography>
                      <Typography fontSize={10} color="text.secondary">{sch.admin_email}</Typography>
                    </TableCell>
                    <TableCell><Chip label={ptLabel(sch.plan)} size="small" sx={{ fontSize:9 }} /></TableCell>
                    <TableCell><Chip label={ptLabel(sch.status)} size="small" color={STATUS_COLOR[sch.status]||'default'} sx={{ fontWeight:700, fontSize:10 }} /></TableCell>
                    <TableCell>{sch.db_provisioned ? <CheckCircle sx={{ fontSize:16, color:'#2e7d32' }} /> : <Warning sx={{ fontSize:16, color:'#e65100' }} />}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.3} alignItems="center">
                        <Typography fontSize={10} fontFamily="monospace" color="primary.main" noWrap sx={{ maxWidth:100 }}>{schoolUrl(sch.slug)}</Typography>
                        <CopyBtn text={schoolUrl(sch.slug)} />
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.3}>
                        <Tooltip title="Manage">
                          <IconButton size="small" onClick={() => { setActionDialog(sch); setActionForm({ action:'', reason:'', plan:sch.plan }); }}>
                            <Build fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="View details">
                          <IconButton size="small" onClick={() => setDetailOpen(sch)}><Visibility fontSize="small" /></IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      {/* Action dialog */}
      <Dialog open={!!actionDialog} onClose={() => setActionDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor:'#0f2b4a', color:'white' }}>Manage — {actionDialog?.school_name}</DialogTitle>
        <DialogContent dividers>
          <Alert severity="info" sx={{ mb:2, fontSize:12 }}>
            Current status: <strong>{ptLabel(actionDialog?.status)}</strong> · Plan: <strong>{ptLabel(actionDialog?.plan)}</strong>
          </Alert>
          <TextField select fullWidth size="small" label="Action" value={actionForm.action} onChange={e => setActionForm(f => ({...f, action:e.target.value}))} sx={{ mb:2 }}>
            <MenuItem value="">Select action…</MenuItem>
            {actionDialog?.status !== 'active' && <MenuItem value="reinstate">✅ Reinstate Account</MenuItem>}
            {actionDialog?.status === 'active' && <MenuItem value="suspend">⏸ Suspend Account</MenuItem>}
            {actionDialog?.status !== 'terminated' && <MenuItem value="terminate">🗑 Terminate Account</MenuItem>}
            <MenuItem value="change_plan">📦 Change Plan</MenuItem>
            {!actionDialog?.db_provisioned && <MenuItem value="retry_provision">🔄 Retry DB Provision</MenuItem>}
            <MenuItem value="fix_settings">🏫 Fix School Name in Settings</MenuItem>
          </TextField>
          {['suspend','terminate'].includes(actionForm.action) && (
            <TextField fullWidth size="small" label="Reason *" value={actionForm.reason} onChange={e => setActionForm(f => ({...f, reason:e.target.value}))} multiline rows={2} sx={{ mb:2 }} />
          )}
          {actionForm.action === 'change_plan' && (
            <TextField select fullWidth size="small" label="New Plan" value={actionForm.plan} onChange={e => setActionForm(f => ({...f, plan:e.target.value}))}>
              {['trial','basic','professional','enterprise'].map(p => <MenuItem key={p} value={p}>{ptLabel(p)}</MenuItem>)}
            </TextField>
          )}
          {actionForm.action === 'terminate' && (
            <Alert severity="error" sx={{ fontSize:12 }}>
              <strong>This is irreversible.</strong> The school account will be locked and users will no longer be able to log in. The database is NOT deleted.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActionDialog(null)}>Cancel</Button>
          <Button variant="contained" color={actionForm.action === 'terminate' ? 'error' : 'primary'}
            onClick={doAction} disabled={!actionForm.action || (['suspend','terminate'].includes(actionForm.action) && !actionForm.reason)}>
            Apply
          </Button>
        </DialogActions>
      </Dialog>

      {/* Detail dialog */}
      <Dialog open={!!detailOpen} onClose={() => setDetailOpen(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{detailOpen?.school_name}</DialogTitle>
        <DialogContent dividers>
          {detailOpen && (
            <Grid container spacing={1.5}>
              {[
                ['Slug', detailOpen.slug], ['Database', detailOpen.db_name],
                ['Login URL', `${PLATFORM_URL}/school/${detailOpen.slug}/login`],
                ['Admin', detailOpen.admin_name], ['Admin Email', detailOpen.admin_email],
                ['Plan', ptLabel(detailOpen.plan)], ['Status', ptLabel(detailOpen.status)],
                ['Trial Ends', fmtD(detailOpen.trial_ends)], ['DB Provisioned', detailOpen.db_provisioned ? 'Yes' : 'No'],
                ['Registered', fmtD(detailOpen.created_at)],
              ].map(([l,v]) => (
                <Grid item xs={6} key={l}>
                  <Typography fontSize={10} color="text.secondary">{l}</Typography>
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Typography fontSize={12} fontWeight={600} fontFamily={['Slug','Database','Login URL'].includes(l)?'monospace':undefined}>{v}</Typography>
                    {['Slug','Login URL'].includes(l) && <CopyBtn text={v} />}
                  </Stack>
                </Grid>
              ))}
            </Grid>
          )}
        </DialogContent>
        <DialogActions><Button onClick={() => setDetailOpen(null)}>Close</Button></DialogActions>
      </Dialog>
    </Box>
  );
}

// ── Registration Requests Tab ──────────────────────────────────────────────────
function RequestsTab() {
  const { enqueueSnackbar } = useSnackbar();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterStatus, setFStatus] = useState('pending');
  const [approveDialog, setApproveDialog] = useState(null);
  const [rejectDialog, setRejectDialog] = useState(null);
  const [approveForm, setApproveForm] = useState({ plan:'trial', admin_password:'', notes:'' });
  const [rejectReason, setRejectReason] = useState('');
  const [approving, setApproving] = useState(false);
  const [result, setResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/platform/requests', { params: { status: filterStatus||undefined } });
      if (r.data.success) setRequests(r.data.data);
    } catch { enqueueSnackbar('Failed to load', { variant:'error' }); }
    finally { setLoading(false); }
  }, [filterStatus, enqueueSnackbar]);

  useEffect(() => { load(); }, [load]);

  const approve = async () => {
    setApproving(true);
    try {
      const r = await api.post(`/platform/requests/${approveDialog.id}/approve`, approveForm);
      setResult(r.data.data);
      enqueueSnackbar('School approved and DB provisioned', { variant:'success' });
      load();
    } catch (err) { enqueueSnackbar(err.response?.data?.message || 'Error', { variant:'error' }); }
    finally { setApproving(false); }
  };

  const reject = async () => {
    try {
      await api.post(`/platform/requests/${rejectDialog.id}/reject`, { rejection_reason: rejectReason });
      enqueueSnackbar('Request rejected', { variant:'success' });
      setRejectDialog(null); load();
    } catch { enqueueSnackbar('Error', { variant:'error' }); }
  };

  return (
    <Box>
      <Stack direction="row" spacing={2} sx={{ mb:2 }} alignItems="center">
        <TextField select size="small" label="Status" value={filterStatus} onChange={e => setFStatus(e.target.value)} sx={{ minWidth:140 }}>
          {['pending','approved','rejected'].map(s => <MenuItem key={s} value={s}>{ptLabel(s)}</MenuItem>)}
          <MenuItem value="">All</MenuItem>
        </TextField>
        <Chip label={`${requests.filter(r=>r.status==='pending').length} pending`} color="warning" size="small" />
        <Box sx={{ flexGrow:1 }} />
        <Tooltip title="Refresh"><IconButton size="small" onClick={load}><Refresh /></IconButton></Tooltip>
      </Stack>

      {loading ? <CircularProgress /> : (
        <Stack spacing={2}>
          {requests.length === 0 && <Paper variant="outlined" sx={{ p:5, textAlign:'center', borderRadius:2 }}><Typography color="text.secondary">No requests</Typography></Paper>}
          {requests.map(req => (
            <Card key={req.id} variant="outlined" sx={{ borderRadius:2, border: req.status==='pending'?'1px solid #fbbf24':undefined }}>
              <CardContent>
                <Grid container spacing={2} alignItems="flex-start">
                  <Grid item xs={12} sm={7}>
                    <Typography fontWeight={700} fontSize={15}>{req.school_name}</Typography>
                    <Typography fontSize={11} color="text.secondary">{req.school_type} · {req.city||''}{req.region?`, ${req.region}`:''}, {req.country}</Typography>
                    <Divider sx={{ my:1 }} />
                    <Grid container spacing={1}>
                      <Grid item xs={6}><Typography fontSize={10} color="text.secondary">Admin</Typography><Typography fontSize={12}>{req.admin_name}</Typography></Grid>
                      <Grid item xs={6}><Typography fontSize={10} color="text.secondary">Admin Email</Typography><Typography fontSize={12}>{req.admin_email}</Typography></Grid>
                      <Grid item xs={6}><Typography fontSize={10} color="text.secondary">Students</Typography><Typography fontSize={12}>{req.student_count||'—'}</Typography></Grid>
                      <Grid item xs={6}><Typography fontSize={10} color="text.secondary">Teachers</Typography><Typography fontSize={12}>{req.teacher_count||'—'}</Typography></Grid>
                    </Grid>
                    {req.motivation && <Typography fontSize={11} color="text.secondary" sx={{ mt:1 }}>"{req.motivation}"</Typography>}
                  </Grid>
                  <Grid item xs={12} sm={5}>
                    <Stack alignItems="flex-end" spacing={1}>
                      <Chip label={ptLabel(req.status)} size="small" color={req.status==='pending'?'warning':req.status==='approved'?'success':'error'} sx={{ fontWeight:700 }} />
                      <Typography fontSize={10} color="text.secondary">Submitted {fmtD(req.created_at)}</Typography>
                      {req.status === 'pending' && (
                        <Stack direction="row" spacing={1}>
                          <Button size="small" variant="contained" color="success" onClick={() => { setApproveDialog(req); setApproveForm({ plan:'trial', admin_password:'', notes:'' }); setResult(null); }}>
                            ✓ Approve
                          </Button>
                          <Button size="small" variant="outlined" color="error" onClick={() => { setRejectDialog(req); setRejectReason(''); }}>
                            ✗ Reject
                          </Button>
                        </Stack>
                      )}
                      {req.status === 'approved' && req.tenant_id && (
                        <Typography fontSize={11} color="success.main">✓ Tenant created</Typography>
                      )}
                    </Stack>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}

      {/* Approve dialog */}
      <Dialog open={!!approveDialog} onClose={() => { setApproveDialog(null); setResult(null); }} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor:'#2e7d32', color:'white' }}>Approve — {approveDialog?.school_name}</DialogTitle>
        <DialogContent dividers>
          {result ? (
            <Box>
              <Alert severity="success" sx={{ mb:2 }}>School approved and database provisioned successfully!</Alert>
              <Grid container spacing={1.5}>
                {[['School URL', result.school_url], ['Slug', result.slug], ['Database', result.db_name], ['Admin Email', result.admin_email], ['Temp Password', result.admin_password]].map(([l,v]) => (
                  <Grid item xs={12} key={l}>
                    <Typography fontSize={11} color="text.secondary">{l}</Typography>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <Typography fontSize={13} fontWeight={600} fontFamily="monospace">{v}</Typography>
                      <CopyBtn text={v} />
                    </Stack>
                  </Grid>
                ))}
              </Grid>
              <Alert severity="warning" sx={{ mt:2, fontSize:12 }}>Share the school URL and temporary password with the admin. They will be prompted to change it on first login.</Alert>
            </Box>
          ) : (
            <Grid container spacing={2} sx={{ pt:1 }}>
              <Grid item xs={12}>
                <Alert severity="info" sx={{ fontSize:12 }}>
                  This will: (1) create a tenant record, (2) provision a new isolated database <strong>sms_{approveDialog?.school_name?.toLowerCase().replace(/\s+/g,'_').slice(0,20)}</strong>, (3) create the admin user account.
                </Alert>
              </Grid>
              <Grid item xs={6}>
                <TextField select fullWidth size="small" label="Plan" value={approveForm.plan} onChange={e => setApproveForm(f => ({...f, plan:e.target.value}))}>
                  {['trial','basic','professional','enterprise'].map(p => <MenuItem key={p} value={p}>{ptLabel(p)}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={6}><TextField fullWidth size="small" label="Temp Admin Password (auto-generated if blank)" value={approveForm.admin_password} onChange={e => setApproveForm(f => ({...f, admin_password:e.target.value}))} /></Grid>
              <Grid item xs={12}><TextField fullWidth size="small" label="Internal Notes" value={approveForm.notes} onChange={e => setApproveForm(f => ({...f, notes:e.target.value}))} multiline rows={2} /></Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setApproveDialog(null); setResult(null); }}>Close</Button>
          {!result && <Button variant="contained" color="success" onClick={approve} disabled={approving}>{approving ? <CircularProgress size={18} color="inherit" /> : 'Approve & Provision'}</Button>}
        </DialogActions>
      </Dialog>

      {/* Reject dialog */}
      <Dialog open={!!rejectDialog} onClose={() => setRejectDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Reject Registration</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" gutterBottom>Rejecting: <strong>{rejectDialog?.school_name}</strong></Typography>
          <TextField fullWidth size="small" label="Reason *" value={rejectReason} onChange={e => setRejectReason(e.target.value)} multiline rows={3} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialog(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={reject} disabled={!rejectReason}>Reject</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ── Ministry Accounts Tab ──────────────────────────────────────────────────────
function MinistryTab() {
  const { enqueueSnackbar } = useSnackbar();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showPw, setShowPw] = useState(false);
  const BLANK = { username:'', password:'', full_name:'', email:'', ministry_name:'Ministry of Education', country:'Eswatini', region:'', is_active:1 };
  const [form, setForm] = useState(BLANK);
  const setF = k => e => setForm(f => ({...f, [k]: e.target.value}));

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get('/platform/ministry-accounts'); if (r.data.success) setAccounts(r.data.data); }
    catch { enqueueSnackbar('Failed to load', { variant:'error' }); }
    finally { setLoading(false); }
  }, [enqueueSnackbar]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      if (editing) { await api.put(`/platform/ministry-accounts/${editing.id}`, form); enqueueSnackbar('Updated', { variant:'success' }); }
      else { await api.post('/platform/ministry-accounts', form); enqueueSnackbar('Account created', { variant:'success' }); }
      setDialog(false); setEditing(null); load();
    } catch (err) { enqueueSnackbar(err.response?.data?.message||'Error', { variant:'error' }); }
  };

  return (
    <Box>
      <Stack direction="row" sx={{ mb:2 }} justifyContent="flex-end">
        <Button variant="contained" startIcon={<Add />} onClick={() => { setEditing(null); setForm({...BLANK}); setDialog(true); }} sx={{ bgcolor:'#0f2b4a' }}>
          Add Ministry Account
        </Button>
      </Stack>

      {loading ? <CircularProgress /> : (
        <Card variant="outlined" sx={{ borderRadius:2 }}>
          <TableContainer>
            <Table size="small">
              <TableHead><TableRow>
                {['Name','Username','Ministry','Country/Region','Status','Last Login',''].map(h => <TableCell key={h} sx={{ bgcolor:'#0f2b4a', color:'white', fontWeight:700, fontSize:11 }}>{h}</TableCell>)}
              </TableRow></TableHead>
              <TableBody>
                {accounts.map((a, i) => (
                  <TableRow key={a.id} hover sx={{ bgcolor: i%2===0?'white':'#fafafa' }}>
                    <TableCell sx={{ fontWeight:600 }}>{a.full_name}</TableCell>
                    <TableCell sx={{ fontFamily:'monospace', fontSize:11 }}>{a.username}</TableCell>
                    <TableCell sx={{ fontSize:12 }}>{a.ministry_name}</TableCell>
                    <TableCell sx={{ fontSize:11 }}>{a.country}{a.region?` · ${a.region}`:''}</TableCell>
                    <TableCell><Chip label={a.is_active?'Active':'Inactive'} size="small" color={a.is_active?'success':'default'} sx={{ fontSize:10 }} /></TableCell>
                    <TableCell sx={{ fontSize:11, color:'text.secondary' }}>{a.last_login ? new Date(a.last_login).toLocaleDateString('en-GB') : 'Never'}</TableCell>
                    <TableCell>
                      <IconButton size="small" onClick={() => { setEditing(a); setForm({ username:a.username, password:'', full_name:a.full_name, email:a.email||'', ministry_name:a.ministry_name, country:a.country, region:a.region||'', is_active:a.is_active }); setDialog(true); }}>
                        <Build fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {accounts.length === 0 && <TableRow><TableCell colSpan={7} align="center" sx={{ py:4, color:'text.secondary' }}>No ministry accounts</TableCell></TableRow>}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      <Dialog open={dialog} onClose={() => setDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit Ministry Account' : 'New Ministry Account'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ pt:1 }}>
            <Grid item xs={12}><TextField fullWidth size="small" label="Full Name" value={form.full_name} onChange={setF('full_name')} /></Grid>
            <Grid item xs={6}><TextField fullWidth size="small" label="Username" value={form.username} onChange={setF('username')} /></Grid>
            <Grid item xs={6}><TextField fullWidth size="small" label="Email" value={form.email} onChange={setF('email')} /></Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label={editing ? 'New Password (leave blank to keep)' : 'Password *'} value={form.password} onChange={setF('password')} type={showPw?'text':'password'}
                InputProps={{ endAdornment:<InputAdornment position="end"><IconButton size="small" onClick={()=>setShowPw(v=>!v)}>{showPw?<VisibilityOff fontSize="small"/>:<Visibility fontSize="small"/>}</IconButton></InputAdornment> }} />
            </Grid>
            <Grid item xs={12}><TextField fullWidth size="small" label="Ministry Name" value={form.ministry_name} onChange={setF('ministry_name')} /></Grid>
            <Grid item xs={6}><TextField fullWidth size="small" label="Country" value={form.country} onChange={setF('country')} /></Grid>
            <Grid item xs={6}><TextField fullWidth size="small" label="Region (restrict view to this region)" value={form.region} onChange={setF('region')} /></Grid>
            <Grid item xs={12}>
              <TextField select fullWidth size="small" label="Status" value={form.is_active} onChange={e => setForm(f => ({...f, is_active:parseInt(e.target.value)}))}>
                <MenuItem value={1}>Active</MenuItem>
                <MenuItem value={0}>Inactive</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={save} sx={{ bgcolor:'#0f2b4a' }}>{editing ? 'Update' : 'Create'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}


// ── Billing Tab ────────────────────────────────────────────────────────────────
function BillingTab() {
  const { enqueueSnackbar } = useSnackbar();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [emailLoading, setEmailLoading] = useState(false);

  useEffect(() => {
    api.get('/platform/billing')
      .then(r => { if (r.data.success) setData(r.data.data); })
      .catch(() => enqueueSnackbar('Failed to load billing', { variant: 'error' }))
      .finally(() => setLoading(false));
  }, []);

  const sendReminders = async () => {
    setEmailLoading(true);
    try {
      const r = await api.post('/platform/send-trial-reminders');
      enqueueSnackbar(r.data.message, { variant: 'success' });
    } catch { enqueueSnackbar('Failed', { variant: 'error' }); }
    finally { setEmailLoading(false); }
  };

  if (loading) return <CircularProgress />;
  const { revenue, by_package, recent_invoices, expiring_trials } = data || {};
  const fmtE = n => `E${parseFloat(n||0).toLocaleString('en-ZA', {minimumFractionDigits:2})}`;

  return (
    <Box>
      {/* Revenue cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Total Revenue', value: fmtE(revenue?.total_revenue), color: '#2e7d32' },
          { label: 'Outstanding', value: fmtE(revenue?.outstanding), color: '#e65100' },
          { label: 'Total Invoices', value: revenue?.total_invoices || 0, color: '#1565c0' },
          { label: 'Overdue', value: revenue?.overdue_count || 0, color: '#c62828' },
        ].map(s => (
          <Grid item xs={6} sm={3} key={s.label}>
            <Card variant="outlined" sx={{ borderRadius: 2, borderLeft: `4px solid ${s.color}` }}>
              <CardContent sx={{ pb: '12px !important' }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" fontSize={9}>{s.label}</Typography>
                <Typography variant="h5" fontWeight={800} sx={{ color: s.color }}>{s.value}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2}>
        {/* Package breakdown */}
        <Grid item xs={12} md={5}>
          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography fontWeight={700} gutterBottom>📦 Revenue by Package</Typography>
              {by_package?.map(p => (
                <Box key={p.code} sx={{ display:'flex', justifyContent:'space-between', py: 0.8, borderBottom:'1px solid #f5f5f5' }}>
                  <Box sx={{ display:'flex', alignItems:'center', gap: 1 }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: p.color }} />
                    <Box>
                      <Typography fontSize={13} fontWeight={600}>{p.name}</Typography>
                      <Typography fontSize={10} color="text.secondary">{p.schools} schools · {fmtE(p.price_annual)}/yr</Typography>
                    </Box>
                  </Box>
                  <Typography fontWeight={700} color="success.main">{fmtE(p.arr)}</Typography>
                </Box>
              ))}
            </CardContent>
          </Card>
        </Grid>

        {/* Trial expiry alerts */}
        <Grid item xs={12} md={7}>
          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography fontWeight={700}>⏰ Trials Expiring (7 days)</Typography>
                <Button size="small" variant="outlined" onClick={sendReminders}
                  disabled={emailLoading} startIcon={emailLoading ? <CircularProgress size={14} /> : null}>
                  Send Reminders
                </Button>
              </Stack>
              {!expiring_trials?.length && (
                <Typography fontSize={12} color="text.secondary">No trials expiring in the next 7 days</Typography>
              )}
              {expiring_trials?.map((t, i) => (
                <Box key={i} sx={{ display:'flex', justifyContent:'space-between', py: 0.6, borderBottom:'1px solid #f5f5f5' }}>
                  <Box>
                    <Typography fontSize={13} fontWeight={600}>{t.school_name}</Typography>
                    <Typography fontSize={10} color="text.secondary">{t.admin_email}</Typography>
                  </Box>
                  <Chip label={`${t.days_left} days left`} size="small"
                    color={t.days_left <= 2 ? 'error' : 'warning'} sx={{ fontSize: 10, fontWeight: 700 }} />
                </Box>
              ))}
            </CardContent>
          </Card>

          {/* Recent invoices */}
          <Card variant="outlined" sx={{ borderRadius: 2, mt: 2 }}>
            <CardContent>
              <Typography fontWeight={700} gutterBottom>🧾 Recent Invoices</Typography>
              {!recent_invoices?.length && <Typography fontSize={12} color="text.secondary">No invoices yet</Typography>}
              {recent_invoices?.slice(0, 6).map((inv, i) => (
                <Box key={i} sx={{ display:'flex', justifyContent:'space-between', py: 0.6, borderBottom:'1px solid #f5f5f5' }}>
                  <Box>
                    <Typography fontSize={12} fontWeight={600}>{inv.invoice_number}</Typography>
                    <Typography fontSize={10} color="text.secondary">{inv.school_name} · {inv.package_name}</Typography>
                  </Box>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography fontSize={12} fontWeight={700}>E{parseFloat(inv.amount).toLocaleString()}</Typography>
                    <Chip label={ptLabel(inv.status)} size="small"
                      color={inv.status==='paid'?'success':inv.status==='overdue'?'error':'warning'}
                      sx={{ fontSize: 9 }} />
                  </Stack>
                </Box>
              ))}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

// ── Main Platform Admin Page ───────────────────────────────────────────────────
export default function PlatformAdmin() {
  const [user, setUser] = useState(() => {
    const u = localStorage.getItem('platform_user');
    return u ? JSON.parse(u) : null;
  });
  const [tab, setTab] = useState(0);

  useEffect(() => {
    const t = localStorage.getItem('platform_token');
    const u = localStorage.getItem('platform_user');
    if (t && u) setUser(JSON.parse(u));
  }, []);

  const handleLogin = u => {
    localStorage.setItem('platform_user', JSON.stringify(u));
    setUser(u);
  };

  const logout = () => {
    localStorage.removeItem('platform_token');
    localStorage.removeItem('platform_user');
    setUser(null);
  };

  if (!user) return <PlatformLogin onLogin={handleLogin} />;

  return (
    <Box sx={{ minHeight:'100vh', bgcolor:'#f5f7fa' }}>
      {/* Top bar */}
      <Box sx={{ bgcolor:'#0f2b4a', px:3, py:1.5, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <AdminPanelSettings sx={{ color:'white', fontSize:28 }} />
          <Box sx={{ ml:1 }}>
            <Typography color="white" fontWeight={700} fontSize={16}>Platform Admin Console</Typography>
            <Typography color="rgba(255,255,255,0.6)" fontSize={11}>School Management SaaS</Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography color="rgba(255,255,255,0.8)" fontSize={12}>{user.name}</Typography>
          <Button size="small" onClick={logout} variant="outlined" sx={{ color:'white', borderColor:'rgba(255,255,255,0.4)', fontSize:11 }}>Logout</Button>
        </Stack>
      </Box>

      <Box sx={{ p:3 }}>
        <Tabs value={tab} onChange={(_,v) => setTab(v)} sx={{ mb:1, borderBottom:1, borderColor:'divider' }}>
          <Tab icon={<BarChart fontSize="small" />}    label="Overview"       iconPosition="start" sx={{ fontSize:12 }} />
          <Tab icon={<School fontSize="small" />}      label="Schools"        iconPosition="start" sx={{ fontSize:12 }} />
          <Tab icon={<Assignment fontSize="small" />}  label="Requests"       iconPosition="start" sx={{ fontSize:12 }} />
          <Tab icon={<People fontSize="small" />}      label="Ministry"       iconPosition="start" sx={{ fontSize:12 }} />
        <Tab icon={<BarChart fontSize="small" />}    label="Billing"        iconPosition="start" sx={{ fontSize:12 }} />
        </Tabs>

        <TabPanel value={tab} index={0}><OverviewTab /></TabPanel>
        <TabPanel value={tab} index={1}><SchoolsTab /></TabPanel>
        <TabPanel value={tab} index={2}><RequestsTab /></TabPanel>
        <TabPanel value={tab} index={3}><MinistryTab /></TabPanel>
        <TabPanel value={tab} index={4}><BillingTab /></TabPanel>
      </Box>
    </Box>
  );
}
